/**
 * Goods Transfer Request Details Component
 * View detailed information about a goods transfer request
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import { useSyncedTab } from '../../../hooks/useSyncedTab';
import DetailLink from '../../Common/DetailLink';
import { useInventory } from '../../../contexts/InventoryContext';
import adminApi from '../../../services/adminApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ConfirmationModal from '../../Common/ConfirmationModal';
import PermissionGuard from '../../Common/PermissionGuard';
import toast from 'react-hot-toast';

const GTRDetails = () => {
  const { id } = useParams();
  const handleBack = useDetailBack('/inventory/goods-transfer-requests');
  const {
    getGoodsTransferRequestById,
    submitGoodsTransferRequestForApproval,
    approveGoodsTransferRequest,
    rejectGoodsTransferRequest,
    cancelGoodsTransferRequest
  } = useInventory();

  const [gtr, setGtr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeTab, setActiveTab] = useSyncedTab('overview');
  const [requestedByUser, setRequestedByUser] = useState(null);

  useEffect(() => {
    loadGTR();
  }, [id]);

  // requestedBy is a raw user id (not populated — see requestedByName comment on the
  // GoodsTransferRequest model). Resolve older records missing requestedByName via the
  // auth-service's no-elevated-permission-required lookup.
  useEffect(() => {
    const requestedById = typeof gtr?.requestedBy === 'string' ? gtr.requestedBy : null;
    if (!gtr || gtr.requestedByName || !requestedById) {
      return;
    }

    let cancelled = false;
    adminApi.getAssignableUsers({ ids: requestedById })
      .then(response => {
        if (cancelled) return;
        const user = response.data?.data?.users?.[0];
        if (user) setRequestedByUser(user);
      })
      .catch(err => console.error('Error resolving requestedBy user:', err));

    return () => { cancelled = true; };
  }, [gtr]);

  const getRequestedByDisplay = () => {
    if (gtr?.requestedByName) return gtr.requestedByName;
    if (requestedByUser) {
      const fullName = `${requestedByUser.firstName || ''} ${requestedByUser.lastName || ''}`.trim();
      return fullName || requestedByUser.email || 'Unknown';
    }
    return 'Unknown';
  };

  const loadGTR = async () => {
    try {
      setLoading(true);
      const response = await getGoodsTransferRequestById(id);
      setGtr(response.data.data || response.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load transfer request details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForApproval = () => {
    setConfirmAction({
      type: 'info',
      title: 'Submit for Approval',
      message: `Are you sure you want to submit "${gtr.requestNo}" for approval? Once submitted, you cannot edit the request.`,
      confirmText: 'Submit',
      onConfirm: async () => {
        try {
          await submitGoodsTransferRequestForApproval(id);
          toast.success('Transfer request submitted for approval');
          loadGTR();
        } catch (err) {
          toast.error('Failed to submit for approval');
        }
      }
    });
  };

  const handleApprove = () => {
    setConfirmAction({
      type: 'success',
      title: 'Approve Transfer Request',
      message: `Are you sure you want to approve "${gtr.requestNo}"?`,
      confirmText: 'Approve',
      onConfirm: async () => {
        try {
          await approveGoodsTransferRequest(id, { comments: 'Approved via web interface' });
          toast.success('Transfer request approved successfully');
          loadGTR();
        } catch (err) {
          toast.error('Failed to approve transfer request');
        }
      }
    });
  };

  const handleReject = () => {
    setConfirmAction({
      type: 'danger',
      title: 'Reject Transfer Request',
      message: `Are you sure you want to reject "${gtr.requestNo}"? Please provide a reason.`,
      confirmText: 'Reject',
      hasInput: true,
      inputLabel: 'Rejection Reason',
      inputRequired: true,
      onConfirm: async (reason) => {
        try {
          await rejectGoodsTransferRequest(id, { reason, comments: 'Rejected via web interface' });
          toast.success('Transfer request rejected');
          loadGTR();
        } catch (err) {
          toast.error('Failed to reject transfer request');
        }
      }
    });
  };

  const handleCancel = () => {
    setConfirmAction({
      type: 'danger',
      title: 'Cancel Transfer Request',
      message: `Are you sure you want to cancel "${gtr.requestNo}"? This action cannot be undone.`,
      confirmText: 'Cancel Request',
      hasInput: true,
      inputLabel: 'Cancellation Reason',
      inputRequired: true,
      onConfirm: async (reason) => {
        try {
          await cancelGoodsTransferRequest(id, reason);
          toast.success('Transfer request cancelled');
          loadGTR();
        } catch (err) {
          toast.error('Failed to cancel transfer request');
        }
      }
    });
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'pending':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'requested':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'approved':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'cancelled':
        return 'bg-gray-100 text-gray-500 border-gray-300';
      case 'converted_to_gi':
        return 'bg-indigo-100 text-indigo-700 border-indigo-300';
      case 'partially_fulfilled':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'fulfilled':
        return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'normal':
        return 'bg-gray-100 text-gray-700 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'InformationCircleIcon' },
    { id: 'lineItems', name: 'Line Items', icon: 'ClipboardListIcon' },
    { id: 'workflow', name: 'Approval Workflow', icon: 'CheckCircleIcon' },
    { id: 'notes', name: 'Notes & Attachments', icon: 'DocumentTextIcon' },
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !gtr) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading transfer request</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error || 'Transfer request not found'}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={handleBack}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm text-red-800 hover:bg-red-200"
                >
                  Back to Transfer Requests
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={handleBack}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{gtr.requestNo}</h1>
              <p className="text-sm text-gray-500">
                Goods Transfer Request • Created {new Date(gtr.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeColor(gtr.status)}`}>
              {gtr.status?.toUpperCase()}
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityBadgeColor(gtr.priority)}`}>
              {gtr.priority?.toUpperCase()} PRIORITY
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex flex-wrap gap-2">
          <PermissionGuard permission="INVENTORY:STOCK_TRANSFERS:UPDATE">
            {gtr.status === 'draft' && (
              <button
                onClick={handleSubmitForApproval}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Submit for Approval
              </button>
            )}
          </PermissionGuard>

          <PermissionGuard permission="INVENTORY:STOCK_TRANSFERS:APPROVE">
            {['pending', 'requested'].includes(gtr.status) && (
              <>
                <button
                  onClick={handleApprove}
                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  Approve
                </button>
                <button
                  onClick={handleReject}
                  className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                >
                  Reject
                </button>
              </>
            )}
          </PermissionGuard>

          <PermissionGuard permission="INVENTORY:STOCK_TRANSFERS:UPDATE">
            {['draft', 'pending', 'requested'].includes(gtr.status) && (
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel Request
              </button>
            )}
          </PermissionGuard>

          <PermissionGuard permission="INVENTORY:STOCK_TRANSFERS:UPDATE">
            {gtr.status === 'draft' && (
              <DetailLink
                to={`/inventory/goods-transfer-requests/edit/${gtr._id}`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Edit Request
              </DetailLink>
            )}
          </PermissionGuard>
        </div>
      </div>

      {/* Progress Indicator */}
      {gtr.status === 'partially_fulfilled' || gtr.status === 'fulfilled' ? (
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-900">Transfer Progress</h3>
            <span className="text-sm text-gray-500">{gtr.completionPercentage || 0}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${gtr.completionPercentage || 0}%` }}
            ></div>
          </div>
        </div>
      ) : null}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'lineItems' && renderLineItems()}
        {activeTab === 'workflow' && renderWorkflow()}
        {activeTab === 'notes' && renderNotes()}
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <ConfirmationModal
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          type={confirmAction.type}
          hasInput={confirmAction.hasInput}
          inputLabel={confirmAction.inputLabel}
          inputRequired={confirmAction.inputRequired}
        />
      )}
    </div>
  );

  function renderOverview() {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Transfer Information</h3>
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">Request No:</span>
              <span className="text-sm text-gray-900 font-mono">{gtr.requestNo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">Request Date:</span>
              <span className="text-sm text-gray-900">
                {new Date(gtr.requestDate).toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">Requested By:</span>
              <span className="text-sm text-gray-900">{getRequestedByDisplay()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">Priority:</span>
              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getPriorityBadgeColor(gtr.priority)}`}>
                {gtr.priority?.toUpperCase()}
              </span>
            </div>
            {gtr.transferReference && (
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Reference:</span>
                <span className="text-sm text-gray-900">{gtr.transferReference}</span>
              </div>
            )}
            {gtr.expectedTransferDate && (
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">Expected Date:</span>
                <span className="text-sm text-gray-900">
                  {new Date(gtr.expectedTransferDate).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Warehouse Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Warehouse Details</h3>
          
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">Source Warehouse</h4>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="font-medium text-gray-900">{gtr.sourceWarehouse?.name}</div>
                <div className="text-sm text-gray-600">{gtr.sourceWarehouse?.code}</div>
                {gtr.sourceWarehouse?.address && (
                  <div className="text-sm text-gray-500 mt-1">{gtr.sourceWarehouse.address}</div>
                )}
              </div>
            </div>

            <div className="flex justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">Destination Warehouse</h4>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="font-medium text-gray-900">{gtr.destinationWarehouse?.name}</div>
                <div className="text-sm text-gray-600">{gtr.destinationWarehouse?.code}</div>
                {gtr.destinationWarehouse?.address && (
                  <div className="text-sm text-gray-500 mt-1">{gtr.destinationWarehouse.address}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Transfer Summary</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{gtr.lineItems?.length || 0}</div>
              <div className="text-sm text-gray-600">Total Items</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{gtr.totalRequestedQuantity || 0}</div>
              <div className="text-sm text-gray-600">Requested Quantity</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{gtr.totalApprovedQuantity || 0}</div>
              <div className="text-sm text-gray-600">Approved Quantity</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{gtr.totalTransferredQuantity || 0}</div>
              <div className="text-sm text-gray-600">Transferred Quantity</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderLineItems() {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Line Items</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-max w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Line
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Batch/Serial
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantities
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {gtr.lineItems?.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.lineNo}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {item.itemDescription}
                    </div>
                    <div className="text-sm text-gray-500">
                      Code: {item.itemCode} | UoM: {item.uom}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.batchLotNo && (
                      <div>Batch: {item.batchLotNo}</div>
                    )}
                    {item.serialNo && (
                      <div>Serial: {item.serialNo}</div>
                    )}
                    {!item.batchLotNo && !item.serialNo && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div>Requested: {item.quantityRequested}</div>
                    {item.quantityApproved > 0 && (
                      <div>Approved: {item.quantityApproved}</div>
                    )}
                    {item.quantityTransferred > 0 && (
                      <div>Transferred: {item.quantityTransferred}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusBadgeColor(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.remarks || <span className="text-gray-400">None</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderWorkflow() {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Approval Workflow</h3>
          
          {gtr.approvalWorkflow && gtr.approvalWorkflow.length > 0 ? (
            <div className="space-y-4">
              {gtr.approvalWorkflow.map((approval, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    approval.status === 'approved' ? 'bg-green-100 text-green-600' :
                    approval.status === 'rejected' ? 'bg-red-100 text-red-600' :
                    approval.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {approval.status === 'approved' ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : approval.status === 'rejected' ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <span className="text-sm font-medium">{approval.level}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      Level {approval.level} - {approval.approver?.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      Status: {approval.status}
                      {approval.approvedAt && (
                        <span> • {new Date(approval.approvedAt).toLocaleDateString()}</span>
                      )}
                    </div>
                    {approval.comments && (
                      <div className="text-sm text-gray-600 mt-1">
                        Comments: {approval.comments}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No approval workflow configured</p>
          )}
        </div>

        {/* Status History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status History</h3>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <div className="text-sm">
                <span className="font-medium">Created</span> - {new Date(gtr.createdAt).toLocaleString()}
                {gtr.metadata?.createdBy?.name && (
                  <span className="text-gray-500"> by {gtr.metadata.createdBy.name}</span>
                )}
              </div>
            </div>
            
            {gtr.metadata?.lastStatusChange && (
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <div className="text-sm">
                  <span className="font-medium">Status changed to {gtr.status}</span> - {new Date(gtr.metadata.lastStatusChange).toLocaleString()}
                </div>
              </div>
            )}

            {gtr.rejectedAt && (
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                <div className="text-sm">
                  <span className="font-medium">Rejected</span> - {new Date(gtr.rejectedAt).toLocaleString()}
                  {gtr.rejectedBy?.name && (
                    <span className="text-gray-500"> by {gtr.rejectedBy.name}</span>
                  )}
                  {gtr.rejectionReason && (
                    <div className="text-gray-600 mt-1">Reason: {gtr.rejectionReason}</div>
                  )}
                </div>
              </div>
            )}

            {gtr.cancelledAt && (
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <div className="text-sm">
                  <span className="font-medium">Cancelled</span> - {new Date(gtr.cancelledAt).toLocaleString()}
                  {gtr.cancelledBy?.name && (
                    <span className="text-gray-500"> by {gtr.cancelledBy.name}</span>
                  )}
                  {gtr.cancelledReason && (
                    <div className="text-gray-600 mt-1">Reason: {gtr.cancelledReason}</div>
                  )}
                </div>
              </div>
            )}

            {gtr.actualTransferDate && (
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <div className="text-sm">
                  <span className="font-medium">Transfer completed</span> - {new Date(gtr.actualTransferDate).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderNotes() {
    return (
      <div className="space-y-6">
        {/* Internal Notes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Internal Notes</h3>
          
          {gtr.internalNotes && gtr.internalNotes.length > 0 ? (
            <div className="space-y-4">
              {gtr.internalNotes.map((note, index) => (
                <div key={index} className="border-l-4 border-gray-200 pl-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">
                      {note.createdBy?.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(note.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{note.note}</p>
                  {note.category && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 mt-2">
                      {note.category}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No internal notes</p>
          )}
        </div>

        {/* Attachments */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Attachments</h3>
          
          {gtr.attachments && gtr.attachments.length > 0 ? (
            <div className="space-y-3">
              {gtr.attachments.map((attachment, index) => (
                <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{attachment.name}</div>
                      <div className="text-xs text-gray-500">
                        {attachment.type} • Uploaded {new Date(attachment.uploadedAt).toLocaleDateString()}
                        {attachment.uploadedBy?.name && (
                          <span> by {attachment.uploadedBy.name}</span>
                        )}
                      </div>
                      {attachment.description && (
                        <div className="text-xs text-gray-600 mt-1">{attachment.description}</div>
                      )}
                    </div>
                  </div>
                  <button className="text-indigo-600 hover:text-indigo-900 text-sm">
                    Download
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No attachments</p>
          )}
        </div>
      </div>
    );
  }
};

export default GTRDetails;